<template>
  <div>
    <!-- MAIN FLOW -->
    <v-container v-if="!collected" class="page-wrap pa-0" fluid>
      <!-- Progress Dialog -->
      <v-dialog v-model="dialogProgress" persistent width="320">
        <v-card class="rounded-xl" color="primary" dark>
          <v-card-text class="pa-5">
            <div class="font-weight-bold mb-2">Processing...</div>
            <div class="text-body-2 mb-3">Please stand by</div>
            <v-progress-linear indeterminate color="white" class="mb-0" />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-row justify="center" no-gutters>
        <v-col cols="12">
          <!-- Header -->
          <v-card v-if="Business" class="card-soft mb-4" outlined>
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <v-avatar
                  v-if="Business.imageUrl"
                  size="46"
                  class="mr-3 flex-shrink-0"
                >
                  <img :src="Business.imageUrl" alt="Logo" />
                </v-avatar>

                <!-- this must be min-w-0 so it can truncate instead of pushing the chip -->
                <div class="min-w-0 flex-grow-1">
                  <div class="text-subtitle-1 font-weight-bold text-truncate">
                    {{ Business.name }}
                  </div>

                  <div
                    v-if="Campaigns"
                    class="text-caption grey--text text--darken-1 text-truncate"
                  >
                    {{ Campaigns.name }}
                  </div>
                </div>

                <!-- chip should never shrink -->
                <v-chip
                  v-if="allRowTotal && Number(allRowTotal) > 0"
                  color="green lighten-4"
                  class="font-weight-bold ml-3 header-total-chip"
                  label
                >
                  <span class="d-none d-sm-inline">Total:</span>
                  <span class="d-sm-none">{{ allRowTotal | currency }}</span>
                  <span class="d-none d-sm-inline ml-1">{{
                    allRowTotal | currency
                  }}</span>
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- Stepper Card -->
          <v-card v-if="Campaigns" class="card-soft" outlined>
            <v-stepper v-model="step" class="elevation-0">
              <!-- STEP 1 -->
              <v-stepper-header class="px-2">
                <v-stepper-step :complete="step > 1" step="1">
                  Sponsor
                </v-stepper-step>
                <v-divider />
                <v-stepper-step :complete="step > 2" step="2">
                  Your Info
                </v-stepper-step>
                <v-divider />
                <v-stepper-step :complete="step > 3" step="3">
                  Payment
                </v-stepper-step>
              </v-stepper-header>

              <v-stepper-items>
                <!-- STEP 1 CONTENT -->
                <v-stepper-content step="1" class="pa-4">
                  <div class="text-subtitle-1 font-weight-bold mb-1">
                    Select a day to sponsor
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-4">
                    Tap a row to choose an amount. Fully reserved days are
                    locked.
                  </div>

                  <!-- Grouped tiles -->
                  <div v-for="(grp, index) in group" :key="index" class="mb-6">
                    <!-- Category header (no box) -->
                    <v-card class="group-header mb-3" flat>
                      <v-card-text class="py-2 px-3">
                        <div class="d-flex align-center">
                          <v-icon small class="mr-2">mdi-calendar-month</v-icon>

                          <div class="text-subtitle-2 font-weight-bold">
                            {{ grp.category }}
                          </div>

                          <v-spacer />

                          <div class="d-flex align-center">
                            <v-chip
                              x-small
                              class="mr-2"
                              color="green lighten-4"
                              text-color="green darken-3"
                            >
                              <v-icon left x-small>mdi-lock</v-icon>
                              {{ groupReservedCount(grp) }} reserved
                            </v-chip>

                            <v-chip
                              x-small
                              color="blue lighten-4"
                              text-color="blue darken-3"
                            >
                              <v-icon left x-small>mdi-calendar-check</v-icon>
                              {{ groupAvailableCount(grp) }} available
                            </v-chip>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>

                    <!-- Tiles grid -->
                    <v-row dense>
                      <v-col
                        v-for="(item, i) in grp.items"
                        :key="i"
                        cols="12"
                        sm="6"
                        md="4"
                        lg="3"
                      >
                        <v-card
                          class="day-tile"
                          :class="rowClass(item)"
                          outlined
                          :ripple="!isPaid(item)"
                          @click="
                            !isPaid(item)
                              ? openRow(item)
                              : onReservedClick(item)
                          "
                        >
                          <v-card-text class="pa-3">
                            <div class="d-flex align-start">
                              <!-- left: progress / check -->
                              <div class="mr-3 flex-shrink-0">
                                <v-progress-circular
                                  v-if="!isPaid(item)"
                                  :size="34"
                                  :width="3"
                                  :value="rowPercent(item)"
                                  color="teal"
                                >
                                  <span class="caption">{{
                                    rowPercent(item)
                                  }}</span>
                                </v-progress-circular>

                                <v-icon v-else size="34" color="teal">
                                  mdi-check-circle-outline
                                </v-icon>
                              </div>

                              <!-- middle + right: controlled layout -->
                              <div class="tile-grid">
                                <!-- LEFT (60%) -->
                                <div class="tile-left">
                                  <!-- Line 1: Title (full width, truncates) -->
                                  <div class="font-weight-bold text-truncate">
                                    {{ item.name }}
                                  </div>

                                  <!-- Line 2: Remaining + chips (wrap ok) -->
                                  <div
                                    class="d-flex align-center flex-wrap mt-1"
                                  >
                                    <div
                                      class="text-caption grey--text text--darken-1 mr-2"
                                    >
                                      Remaining:
                                      <span class="font-weight-bold">
                                        {{
                                          (item.amount - (item.received || 0))
                                            | currency
                                        }}
                                      </span>
                                    </div>

                                    <!-- received chip (move to next line if needed) -->
                                    <v-chip
                                      v-if="
                                        !isPaid(item) &&
                                        item.received &&
                                        Number(item.received) > 0
                                      "
                                      color="amber lighten-3"
                                      class="ml-0 mt-1"
                                      x-small
                                    >
                                      {{ item.received | currency }} received
                                    </v-chip>

                                    <!-- reserved chip -->
                                    <v-chip
                                      v-if="isPaid(item)"
                                      color="green"
                                      text-color="white"
                                      class="ml-0 mt-1"
                                      x-small
                                    >
                                      RESERVED
                                    </v-chip>
                                  </div>

                                  <!-- Optional: Your amount -->
                                  <div
                                    v-if="
                                      item.donationAmount &&
                                      Number(item.donationAmount) > 0
                                    "
                                    class="text-caption mt-1 teal--text text--darken-3 font-weight-bold"
                                  >
                                    Your amount:
                                    {{ item.donationAmount | currency }}
                                  </div>
                                </div>

                                <!-- RIGHT (40%) no wrap -->
                                <div class="tile-right">
                                  <div
                                    class="text-caption grey--text text--darken-1 tile-right-label"
                                  >
                                    To sponsor
                                  </div>
                                  <div
                                    class="font-weight-bold tile-right-amount"
                                  >
                                    {{
                                      (item.amount - (item.received || 0))
                                        | currency
                                    }}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- small progress bar for quick glance -->
                            <v-progress-linear
                              class="mt-3"
                              height="6"
                              rounded
                              :value="rowPercent(item)"
                              color="teal"
                              background-opacity="0.12"
                            />
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Total summary -->
                  <v-card
                    class="card-soft pa-3 mt-2"
                    color="green lighten-5"
                    outlined
                  >
                    <div class="d-flex align-center">
                      <div>
                        <div class="text-caption grey--text text--darken-1">
                          Your Donation Total
                        </div>
                        <div class="text-h6 font-weight-bold">
                          {{ allRowTotal | currency }}
                        </div>
                      </div>
                      <v-spacer />
                      <v-btn
                        color="primary"
                        large
                        rounded
                        class="px-6"
                        :disabled="!allowStep2"
                        @click="infoStep"
                      >
                        Next
                      </v-btn>
                    </div>
                  </v-card>

                  <div class="d-flex align-center mt-4">
                    <v-btn text @click="$router.go(-1)">Back</v-btn>
                    <v-spacer />
                    <div class="text-caption grey--text text--darken-1">
                      Select at least one day to continue
                    </div>
                  </div>
                </v-stepper-content>

                <!-- STEP 2 CONTENT -->
                <v-stepper-content step="2" class="pa-4">
                  <div class="text-subtitle-1 font-weight-bold mb-1">
                    Your Information
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-4">
                    We’ll send a receipt to your email.
                  </div>

                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        autofocus
                        v-model="Donor.name"
                        :error-messages="nameErrors"
                        :hide-details="nameErrors.length === 0"
                        outlined
                        dense
                        label="Name"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        type="number"
                        v-model="Donor.phoneNumber"
                        :error-messages="phoneNumberErrors"
                        :hide-details="phoneNumberErrors.length === 0"
                        outlined
                        dense
                        label="Phone Number"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="Donor.email"
                        :error-messages="emailErrors"
                        :hide-details="emailErrors.length === 0"
                        outlined
                        dense
                        label="Email Address"
                      />
                    </v-col>
                  </v-row>

                  <v-divider class="my-4" />

                  <div class="d-flex align-center">
                    <v-btn text large @click="step = 1">Back</v-btn>
                    <v-spacer />
                    <v-btn
                      large
                      color="primary"
                      rounded
                      class="px-6"
                      @click="paymentInfo"
                    >
                      Next
                    </v-btn>
                  </div>
                </v-stepper-content>

                <!-- STEP 3 CONTENT -->
                <v-stepper-content step="3" class="pa-4">
                  <div class="text-subtitle-1 font-weight-bold mb-1">
                    Payment
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-4">
                    Enter your card details to complete the donation.
                  </div>

                  <!-- <CreditCard
                    v-if="step === 3"
                    @submit="infoCollect"
                    :localCollectOnly="true"
                    :donor="Donor"
                    :public="true"
                    :donationCategoryId="
                      Business ? Business.DefaultDonationId : null
                    "
                  /> -->

                  <CreditCard
                    v-if="step === 3"
                    @submit="infoCollect"
                    :localCollectOnly="true"
                    :donor="Donor"
                    :public="true"
                    :donationCategoryId="
                      Business ? Business.DefaultDonationId : null
                    "
                    :hideBank="true"
                  />

                  <p class="mt-4 text-body-2" v-if="Business">
                    By clicking “Donate”, you authorize an electronic fund
                    transfer for this donation to {{ Business.name }}.
                  </p>

                  <v-divider class="my-4" />

                  <div class="d-flex align-center">
                    <v-btn text large @click="step = 2">Back</v-btn>
                    <v-spacer />
                    <v-btn
                      large
                      color="success"
                      rounded
                      class="px-7"
                      @click="submit"
                      :disabled="loading"
                      :loading="loading"
                    >
                      Donate {{ totalDonationAmount | currency }}
                    </v-btn>
                  </div>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </v-card>

          <!-- Sticky summary bar (mobile helpful) -->
          <div
            class="sticky-bar"
            v-if="step === 1 && allRowTotal && allRowTotal > 0"
          >
            <div class="sticky-inner">
              <div class="min-w-0">
                <div class="text-caption grey--text text--darken-1">Total</div>
                <div class="font-weight-bold">{{ allRowTotal | currency }}</div>
              </div>
              <v-spacer />
              <v-btn
                color="primary"
                rounded
                class="px-6"
                :disabled="!allowStep2"
                @click="infoStep"
              >
                Next
              </v-btn>
            </div>
          </div>

          <!-- Select Day dialog -->
          <DaySponsorDialog
            v-model="dialog"
            :row="selectedRow"
            :quickAmounts="preDefinedValue"
            @close="close"
            @paid-in-full="paidInFull"
            @submit="onDialogSubmit"
          />
        </v-col>
      </v-row>
    </v-container>

    <!-- SUCCESS -->
    <v-container v-else :fluid="$vuetify.breakpoint.xsOnly" class="page-wrap">
      <v-row justify="center" no-gutters>
        <v-col cols="12" sm="10" md="8" lg="6">
          <v-card class="card-soft" outlined>
            <v-card-text class="pa-6">
              <lottie
                :height="200"
                :options="defaultOptions"
                name="receiptCheck"
                v-on:animCreated="handleAnimation"
                @onComplete="showHomeButton = true"
              />
              <div class="text-center">
                <div class="text-h5 font-weight-bold">All Set</div>
                <div class="mt-3 text-body-2">
                  The Prophet, upon be peace, said: “The believer’s shade on the
                  Day of Resurrection will be his charity.”
                  <span class="font-weight-bold">Al-Tirmidhi</span>
                </div>
              </div>

              <div class="text-center mt-5" v-if="showHomeButton">
                <v-slide-y-transition>
                  <v-btn
                    block
                    large
                    @click="$router.go(-1)"
                    color="success"
                    rounded
                  >
                    Home
                  </v-btn>
                </v-slide-y-transition>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import donationService from "@/modules/Campaign/service.js";
import CreditCard from "@/modules/PaymentMethod/components/CreditCard.vue";
import Lottie from "@/components/lottie.vue";
import * as animationData from "@/assets/lottie/1708-success.json";

import DaySponsorDialog from "./DaySponsorDialog.vue";
const DONOR_STORAGE_KEY = "taleem_public_donor_v1";
const { validationMixin } = require("vuelidate");
const { required, email } = require("vuelidate/lib/validators");

export default {
  name: "collectDonationByCampaign",
  components: { CreditCard, Lottie, DaySponsorDialog },
  mixins: [validationMixin],
  validations: {
    Donor: {
      name: { required },
      phoneNumber: { required },
      email: { required, email },
    },
  },
  data() {
    return {
      donorSaveTimer: null,
      dialogProgress: false,
      donationLimit: false,
      paidInFullAmount: 0,
      allRowTotal: 0,
      donationSelectedAmount: 0,
      dialog: false,
      selectedRow: null,
      Donor: { name: null, phoneNumber: null, email: null },

      startingDate: this.$moment().add(1, "day"),
      group: [],
      collected: false,
      card: null,
      showHomeButton: false,

      loading: false,
      feeAmount: 0,
      allowStep2: false,
      totalDonationAmount: 0,
      step: 1,

      Campaigns: null,
      Business: null,

      preDefinedValue: ["10", "20", "50", "100", "150", "200"],

      defaultOptions: {
        animationData: animationData.default,
        loop: false,
        autoplay: true,
      },
    };
  },
  created() {
    this.loadDonorFromLocal();
    this.loadCampaign();
  },
  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.Donor.name.$dirty) return errors;
      if (!this.$v.Donor.name.required) errors.push("Name is required");
      return errors;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.Donor.phoneNumber.$dirty) return errors;
      if (!this.$v.Donor.phoneNumber.required)
        errors.push("Phone Number is required");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.Donor.email.$dirty) return errors;
      if (!this.$v.Donor.email.required)
        errors.push("Email Address is required");
      if (!this.$v.Donor.email.email) errors.push("Email is not valid");
      return errors;
    },
  },
  watch: {
    Donor: {
      deep: true,
      handler() {
        // tiny debounce so it doesn't write every keystroke instantly
        clearTimeout(this.donorSaveTimer);
        this.donorSaveTimer = setTimeout(() => {
          this.saveDonorToLocal();
        }, 250);
      },
    },
    allRowTotal(val) {
      this.allowStep2 = val > 0;
    },
    dialog(val) {
      if (val && this.selectedRow) {
        const received = this.selectedRow.received
          ? parseFloat(this.selectedRow.received)
          : 0;
        this.paidInFullAmount = parseFloat(this.selectedRow.amount) - received;

        // rebuild quick values cleanly (avoid pushing duplicates)
        this.resetQuickAmounts();
        this.preDefinedValue.push(String(this.paidInFullAmount.toFixed(2)));
      }
    },
    donationSelectedAmount(val) {
      if (!this.selectedRow) return;
      const picked = this.preDefinedValue[val];
      this.selectedRow.donationAmount = picked;
    },
  },
  methods: {
    saveDonorToLocal() {
      try {
        const payload = {
          name: (this.Donor?.name || "").trim(),
          phoneNumber: (this.Donor?.phoneNumber || "").toString().trim(),
          email: (this.Donor?.email || "").trim(),
          updatedAt: new Date().toISOString(),
        };

        // don’t save empty junk
        if (!payload.name && !payload.phoneNumber && !payload.email) return;

        localStorage.setItem(DONOR_STORAGE_KEY, JSON.stringify(payload));
      } catch (e) {
        // ignore storage errors
      }
    },

    loadDonorFromLocal() {
      try {
        const raw = localStorage.getItem(DONOR_STORAGE_KEY);
        if (!raw) return;

        const saved = JSON.parse(raw) || {};

        // only fill missing fields so we don’t override user typing
        if (!this.Donor.name && saved.name) this.Donor.name = saved.name;
        if (!this.Donor.phoneNumber && saved.phoneNumber)
          this.Donor.phoneNumber = saved.phoneNumber;
        if (!this.Donor.email && saved.email) this.Donor.email = saved.email;
      } catch (e) {
        // ignore parse errors
      }
    },

    clearDonorLocal() {
      try {
        localStorage.removeItem(DONOR_STORAGE_KEY);
      } catch (e) {
        console.log("error", e);
      }
    },
    collectedAmount(item) {
      // if reserved, show the full goal amount (clean + always correct)
      return Number(item?.amount || 0);
    },
    dayLabel(item) {
      if (!item?.date) return null;
      return this.$moment(item.date).format("ddd, MMM D"); // Sat, Mar 1
    },
    templateLookup() {
      const meta = this.Campaigns?.metadata || {};
      const t = Array.isArray(meta.ramadanTemplate) ? meta.ramadanTemplate : [];
      const map = {};

      for (const r of t) {
        const key = `${r.category}__${r.name}`.toLowerCase();
        map[key] = r;
      }
      return map;
    },

    enrichDetail(detail, lookup) {
      const key = `${detail.category}__${detail.name}`.toLowerCase();
      const t = lookup[key] || {};

      // keep whatever is coming from backend, but add extra fields
      return {
        ...detail,
        // you already have amount/received from backend, but template can be fallback
        amount: detail.amount ?? t.amount,
        date: detail.date ?? t.date ?? null,
        visitors: detail.visitors ?? t.visitors ?? null,
      };
    },
    groupReservedCount(grp) {
      if (!grp || !grp.items) return 0;
      let count = 0;
      for (let i = 0; i < grp.items.length; i++) {
        if (this.isPaid(grp.items[i])) count++;
      }
      return count;
    },
    groupAvailableCount(grp) {
      if (!grp || !grp.items) return 0;
      let count = 0;
      for (let i = 0; i < grp.items.length; i++) {
        if (!this.isPaid(grp.items[i])) count++;
      }
      return count;
    },
    onDialogSubmit(amount) {
      // update selectedRow donation amount and keep your existing flow
      this.selectedRow.donationAmount = amount;
      this.submitDay();
    },
    onReservedClick(item) {
      const dayName = item?.name || "This day";

      this.$swal.fire({
        icon: "success",
        title: `${dayName} is already reserved`,
        html: `
      <div style="text-align:left; line-height:1.5;">
        <div style="margin-bottom:10px;">
          Alhamdulillah, this day has been fully sponsored by someone already.
        </div>

        <div style="margin-bottom:10px;">
          You can still earn reward by sponsoring another day.
          Please pick any available day and take part in this sadaqah.
        </div>

        <div style="font-size:13px; opacity:0.8;">
          Tip: Available days show a remaining amount.
        </div>
      </div>
    `,
        confirmButtonText: "Choose another day",
        showCancelButton: true,
        cancelButtonText: "Close",
        confirmButtonColor: "#2e7d32",
        width: 520,
      });
    },
    rowPercent(item) {
      return this.calculateReceivedingPercentage(item.received, item.amount);
    },
    isPaid(item) {
      return (
        this.calculateReceivedingPercentage(item.received, item.amount) >= 100
      );
    },
    isSelected(item) {
      return item.donationAmount && Number(item.donationAmount) > 0;
    },
    rowClass(item) {
      return {
        selectedRow: this.isSelected(item),
        receiveMoney: item.received && Number(item.received) > 0,
        paidRow: this.isPaid(item),
      };
    },

    resetQuickAmounts() {
      this.preDefinedValue = ["10", "20", "50", "100", "150", "200"];
      this.donationSelectedAmount = 0;
    },

    reCalculateFee() {
      this.feeAmount = (
        (parseFloat(this.allRowTotal) * 3.4) / 100 +
        0.3
      ).toFixed(2);

      // keep existing behavior (final total = allRowTotal)
      this.totalDonationAmount = parseFloat(this.allRowTotal);
    },

    paidInFull() {
      if (!this.selectedRow) return;
      this.selectedRow.donationAmount = this.paidInFullAmount;
      this.submitDay();
    },

    handleAnimation(anim) {
      this.anim = anim;
    },

    calculateReceivedingPercentage(receivedingAmount, totalAmount) {
      let receive = 0;
      if (receivedingAmount) receive = receivedingAmount;
      const percentage = (receive / totalAmount) * 100;
      return parseFloat(Number(percentage).toFixed(0));
    },

    close() {
      this.resetQuickAmounts();
      this.dialog = false;
      this.setDonationAmount();
    },

    submitDay() {
      const selected = this.selectedRow;
      if (!selected) return;

      for (let c = 0; c < this.group.length; c++) {
        const cateogry = this.group[c];
        for (let r = 0; r < cateogry.items.length; r++) {
          const row = cateogry.items[r];
          if (row.id === selected.id) {
            row.donationAmount = selected.donationAmount;
          }
        }
      }

      this.close();
      this.$forceUpdate();
    },

    openRow(item) {
      this.selectedRow = JSON.parse(JSON.stringify(item));

      // default
      this.selectedRow.donationAmount = "10";

      if (
        this.calculateReceivedingPercentage(item.received, item.amount) < 100
      ) {
        this.dialog = true;
      } else {
        this.$swal.fire({
          title: item.name,
          text: "Reserved and paid in full",
          icon: "info",
        });
      }
    },

    groupByCategory(data) {
      const result = [];
      const temp = {};
      const lookup = this.templateLookup();

      for (let i = 0; i < data.CampaignDetails.length; i++) {
        const raw = data.CampaignDetails[i];
        const item = this.enrichDetail(raw, lookup);

        const category = item.category;
        if (!temp[category]) temp[category] = [];

        temp[category].push({
          id: item.id,
          name: item.name,
          amount: item.amount,
          received: item.received,
          donationAmount: item.donationAmount || 0,

          // NEW
          date: item.date,
          visitors: item.visitors,
        });
      }

      for (const category in temp) {
        result.push({ category, items: temp[category] });
      }
      return result;
    },

    infoCollect(val) {
      this.card = val;
    },

    submit() {
      try {
        const data = {};
        data.Donor = this.Donor;

        this.$events.emit("submitCreditCard");

        data.donationLimit = this.donationLimit;
        data.Donor = this.Donor;
        data.card = this.card;
        data.campaignType = this.Campaigns.type;
        data.selectedRow = [];

        // keep existing behavior
        data.processingFeeCollect = false;

        for (let c = 0; c < this.group.length; c++) {
          const cateogry = this.group[c];
          for (let r = 0; r < cateogry.items.length; r++) {
            const row = cateogry.items[r];
            if (row.donationAmount && Number(row.donationAmount) > 0) {
              data.selectedRow.push(row);
            }
          }
        }

        data.amount = this.allRowTotal;
        data.feeAmount = this.feeAmount;
        data.finalAmount = parseFloat(data.amount);
        data.CampaignId = this.Campaigns.id;

        if (!data.card) return false;

        this.loading = true;
        this.dialogProgress = true;

        donationService
          .publicDonationNoLimit(this.Business.code, data)
          .then(() => {
            this.collected = true;
            this.loading = false;
          })
          .catch((error) => {
            this.loading = false;
            this.dialogProgress = false;

            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title:
                  error && error.data && error.data.message
                    ? error.data.message
                    : "Payment failed",
              });
          });
      } catch (e) {
        this.dialogProgress = false;
        this.loading = false;
        console.log("submit error", e);
      }
    },

    paymentInfo() {
      this.$v.Donor.$touch();
      if (this.$v.Donor.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.saveDonorToLocal();
        this.step = 3;
      }
    },

    setDonationAmount() {
      if (this.Campaigns && this.Campaigns.type === "sponsor") {
        if (this.step === 1 && this.group.length) {
          this.allRowTotal = 0;

          for (let c = 0; c < this.group.length; c++) {
            const category = this.group[c];
            for (let r = 0; r < category.items.length; r++) {
              const row = category.items[r];
              if (row.donationAmount)
                this.allRowTotal += parseFloat(row.donationAmount);
              if (!this.donationLimit && row.qty > 0)
                this.allRowTotal += parseFloat(row.amount) * row.qty;
            }
          }
        }

        this.feeAmount = 0;
        this.reCalculateFee();
      }
    },

    infoStep() {
      this.step = 2;
    },

    loadCampaign() {
      const code = this.$route.params.id;

      donationService
        .getCampaignById(code)
        .then((response) => {
          console.log("no limite loading", response);
          this.Campaigns = response.data;
          this.Business = response.data.Business;

          // predefined values
          const meta =
            this.Campaigns && this.Campaigns.metadata
              ? this.Campaigns.metadata
              : null;
          if (meta && meta.preDefinedValue && meta.preDefinedValue.length) {
            this.preDefinedValue = meta.preDefinedValue;
          }

          if (meta && meta.donationLimit) this.donationLimit = true;

          this.group = this.groupByCategory(this.Campaigns);
          this.setDonationAmount();
        })
        .catch((error) => {
          console.log("error getting campaign", error);
        });
    },
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 16px;
  padding-bottom: 110px;
}

.card-soft {
  border-radius: 18px;
  overflow: hidden;
}

.rounded-xl {
  border-radius: 18px !important;
}

.min-w-0 {
  min-width: 0;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section-title {
  padding: 10px 12px;
  border-radius: 14px;
  background: #f5f5f5;
}

.row-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.selectedRow {
  background: #e0f2f1;
}

.receiveMoney {
  background: #fff9c4;
}

.paidRow {
  background: #c8e6c9;
}

/* Sticky bar */
.sticky-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 12px;
  z-index: 20;
}

.sticky-inner {
  max-width: 760px;
  margin: 0 auto;
  background: white;
  border-radius: 18px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.day-tile {
  border-radius: 16px;
  overflow: hidden;
}

.min-w-0 {
  min-width: 0;
}
.header-total-chip {
  flex: 0 0 auto; /* don’t shrink */
  white-space: nowrap; /* keep it on one line */
  max-width: 100%;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Your existing highlight states still work */
.selectedRow {
  background: #e0f2f1;
}

.receiveMoney {
  background: #fff9c4;
}

.paidRow {
  background: #c8e6c9;
}

.group-header {
  border-radius: 14px;
  background: #f5f6f7; /* soothing light gray */
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.group-header .v-icon {
  opacity: 0.8;
}

.group-header .text-caption {
  color: rgba(0, 0, 0, 0.6);
}
.tile-grid {
  display: flex;
  gap: 12px;
  width: 100%;
  min-width: 0; /* lets truncation work */
}

.tile-left {
  flex: 0 1 60%;
  min-width: 0;
}

.tile-right {
  flex: 0 0 40%;
  text-align: right;
  white-space: nowrap; /* prevents wrap */
}

.tile-right-label,
.tile-right-amount {
  white-space: nowrap;
}
</style>